import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Slide,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import ShieldIcon from '@mui/icons-material/Shield';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { usersAPI } from 'services/api';

/**
 * Fade-up transition for the dialog.
 */
const FadeUpTransition = React.forwardRef(function FadeUpTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * WelcomeOnboardingModal
 *
 * Fullscreen modal shown ONCE when:
 *   - user.kyc_status === 'VERIFIED' (case-insensitive)
 *   - user.first_login_completed === false
 *
 * On dismiss it calls PATCH /api/v1/users/me/complete-first-login,
 * sets first_login_completed = true in AuthContext, and never
 * reappears for that user.
 */
const WelcomeOnboardingModal = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  /* ── Visibility check ── */
  const kycVerified =
    user?.kyc_status &&
    String(user.kyc_status).toUpperCase() === 'VERIFIED';

  const isFirstLogin = user?.first_login_completed === false;

  const open = Boolean(kycVerified && isFirstLogin);

  /* ── Complete first login (shared by both buttons & close) ── */
  const completeFirstLogin = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await usersAPI.completeFirstLogin();
    } catch (err) {
      // API failure is non-blocking; still mark locally so modal
      // closes. Next login the backend will still see the flag.
      console.error('Failed to mark first login complete:', err);
    }
    updateUser({ first_login_completed: true });
    setSubmitting(false);
  }, [submitting, updateUser]);

  /* ── Button handlers ── */
  const handleCreateFarm = async () => {
    await completeFirstLogin();
    navigate('/farms/create');
  };

  const handleGoToDashboard = async () => {
    await completeFirstLogin();
    navigate('/farmer/dashboard');
  };

  /* ── Don't render anything when conditions not met ── */
  if (!open) return null;

  return (
    <Dialog
      open={open}
      fullScreen
      TransitionComponent={FadeUpTransition}
      TransitionProps={{ timeout: 500 }}
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0fdf4 100%)',
        },
      }}
    >
      <DialogContent
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}
      >
        {/* Card container */}
        <div className="w-full max-w-lg mx-auto px-6 py-10 flex flex-col items-center text-center animate-fade-in">
          {/* Success badge */}
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6 shadow-lg shadow-green-200 transition-transform duration-700 ease-out scale-100 hover:scale-105">
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#16a34a' }} />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">
            Welcome to MeenSure 🎉
          </h1>

          {/* Subtitle */}
          <p className="text-base text-gray-600 mb-8 max-w-md leading-relaxed">
            Your KYC has been successfully verified. You are now eligible for:
          </p>

          {/* Feature cards */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <FeatureCard
              icon={<AccountBalanceIcon sx={{ fontSize: 32, color: '#2563eb' }} />}
              label="Credit Cultivation"
              color="blue"
            />
            <FeatureCard
              icon={<ShieldIcon sx={{ fontSize: 32, color: '#7c3aed' }} />}
              label="Insurance Protection"
              color="purple"
            />
            <FeatureCard
              icon={<StorefrontIcon sx={{ fontSize: 32, color: '#059669' }} />}
              label="Vendor Marketplace"
              color="green"
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
            {/* Primary — Create Farm */}
            <button
              onClick={handleCreateFarm}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-base shadow-lg shadow-blue-200 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <>
                  <AgricultureIcon sx={{ fontSize: 20 }} />
                  Create Farm
                  <ArrowForwardIcon sx={{ fontSize: 18 }} />
                </>
              )}
            </button>

            {/* Secondary — Dashboard */}
            <button
              onClick={handleGoToDashboard}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-300 hover:border-blue-400 bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-700 font-semibold text-base transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: '#6b7280' }} />
              ) : (
                <>
                  <DashboardIcon sx={{ fontSize: 20 }} />
                  Go to Dashboard
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Feature card sub-component ── */
const colorMap = {
  blue:   { bg: 'bg-blue-50',   ring: 'ring-blue-100',   text: 'text-blue-700' },
  purple: { bg: 'bg-purple-50', ring: 'ring-purple-100', text: 'text-purple-700' },
  green:  { bg: 'bg-green-50',  ring: 'ring-green-100',  text: 'text-green-700' },
};

const FeatureCard = ({ icon, label, color = 'blue' }) => {
  const c = colorMap[color] || colorMap.blue;
  return (
    <div
      className={`flex flex-col items-center gap-2 p-5 rounded-2xl ${c.bg} ring-1 ${c.ring} transition-transform duration-300 hover:scale-105`}
    >
      {icon}
      <span className={`text-sm font-semibold ${c.text}`}>{label}</span>
    </div>
  );
};

export default WelcomeOnboardingModal;
